import { useDispatch, useSelector } from "react-redux";
import { Appstore } from "../../redux";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { SetFirms } from "../../redux/catalogs/states";
import { getAllFirms } from "../../Services/Firm";
import IFirm from "../../interfaces/models/IFirm";
import { useAuth } from "../../store/AuthContext";
import { getCookie } from "../../utils/cookies";
import { useStaffFirmSurface } from "../useStaffFirmSurface";
import { firmAudienceName, firmLongName } from "../../utils/firmDisplayName";

export enum firmType {
  member = "member",
  nonMember = "nonMember",
  both = "both",
}

/** Dedupes concurrent firm list requests across every `UseFirmList` instance. */
let sharedFirmsRequest: Promise<IFirm[]> | null = null;

const RETRY_DELAYS_MS = [0, 500, 1500, 3000];

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const hasAuthToken = (): boolean => {
  if (typeof window === "undefined") {
    return false;
  }
  return Boolean(sessionStorage.getItem("token") || getCookie("token"));
};

async function fetchFirmsShared(bustCache = false): Promise<IFirm[]> {
  if (bustCache) {
    sharedFirmsRequest = null;
  }
  if (!sharedFirmsRequest) {
    sharedFirmsRequest = getAllFirms()
      .then((data) => {
        const firms = Array.isArray(data) ? data : [];
        if (firms.length === 0) {
          sharedFirmsRequest = null;
        }
        return firms;
      })
      .catch((error) => {
        sharedFirmsRequest = null;
        throw error;
      });
  }
  return sharedFirmsRequest;
}

const UseFirmList = (firmTypes: firmType = firmType.both) => {
  const dispatch = useDispatch();
  const firms = useSelector((state: Appstore) => state.firms);
  const { isAuthenticated } = useAuth();
  const location = useLocation();
  const staffSurface = useStaffFirmSurface();
  const [isFetching, setIsFetching] = useState(false);

  const isMemberSearchPage = location.pathname === "/member-search";

  const refetchFirms = useCallback((): Promise<void> => {
    if (!hasAuthToken()) {
      return Promise.resolve();
    }
    setIsFetching(true);
    return fetchFirmsShared(true)
      .then((response) => {
        dispatch(SetFirms(response));
        if (!response.length) {
          console.error("No firms found. Please check the data source.");
        }
      })
      .catch((error) => {
        console.error(error);
        throw error;
      })
      .finally(() => setIsFetching(false));
  }, [dispatch]);

  const canFetch = isAuthenticated || hasAuthToken() || isMemberSearchPage;

  useEffect(() => {
    if (firms.length > 0) {
      setIsFetching(false);
      return;
    }
    if (!canFetch) {
      return;
    }

    let cancelled = false;
    setIsFetching(true);

    const load = async () => {
      for (let attempt = 0; attempt < RETRY_DELAYS_MS.length; attempt++) {
        if (cancelled) {
          return;
        }
        if (attempt > 0) {
          await sleep(RETRY_DELAYS_MS[attempt]);
        }
        if (cancelled) {
          return;
        }
        try {
          const response = await fetchFirmsShared(attempt > 0);
          dispatch(SetFirms(response));
          if (response.length > 0) {
            return;
          }
          if (attempt === RETRY_DELAYS_MS.length - 1) {
            console.error("No firms found. Please check the data source.");
          }
        } catch (error) {
          if (attempt === RETRY_DELAYS_MS.length - 1) {
            console.error(error);
          }
        }
      }
    };

    void load().finally(() => {
      if (!cancelled) {
        setIsFetching(false);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [firms.length, dispatch, canFetch]);

  const firmData = useMemo(() => {
    let list = firms;
    if (firmTypes === firmType.member) {
      list = firms.filter((f) => f.isMember === true);
    } else if (firmTypes === firmType.nonMember) {
      list = firms.filter((f) => f.isMember === false);
    }
    return list.map((firm) => {
      const longName = firmLongName(firm);
      return {
        ...firm,
        longName,
        name: firmAudienceName(firm, staffSurface) || longName,
      };
    });
  }, [firms, firmTypes, staffSurface]);

  return { firmData, isFetching, refetchFirms, firmsLoaded: firms.length > 0 };
};

export { UseFirmList };
